/**
 * AddAdvanceBillSlideOver — file a vendor bill against an open
 * staff advance.
 *
 * Slim cousin of `CreateTransactionSlideOver`. The form only
 * collects vendor + amount + category + date + reference +
 * description — type is locked to `payment_out`, the parent
 * advance ID is fixed by the caller, and submission routes
 * through `addAdvanceSettlement` (which wraps a runTransaction
 * to atomically flip the parent's advanceStatus → 'partial').
 *
 * Header surfaces the advance context (given to whom, original
 * amount, current unsettled balance) so the studio admin always
 * has the wallet they're spending from in view.
 */
import { useEffect, useMemo, useState } from 'react';

import { canFinance } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { PartyPickerSlideOver } from '@/components/PartyPickerSlideOver';
import { partyTypeColors, partyTypeLabel, type PartyType } from '@/lib/parties';
import {
  PAYMENT_METHOD_OPTIONS,
  TRANSACTION_CATEGORY_OPTIONS,
  addAdvanceSettlement,
  computeAdvanceBalance,
  type PaymentMethod,
  type TransactionCategory,
} from '@/lib/transactions';
import { useAdvanceChildren } from '@/lib/useAdvances';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import type { TransactionItem } from '@/lib/useProjectTransactions';

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const STYLE_LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--text-secondary)',
};
const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };
const STYLE_GROUP: React.CSSProperties = { marginTop: 12 };

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtInr(n: number | undefined | null): string {
  if (!n) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export type AddAdvanceBillSlideOverProps = {
  open: boolean;
  onClose: () => void;
  /** The parent staff_advance transaction this bill applies to.
   *  `null` keeps the slide-over closed regardless of `open`. */
  advance: TransactionItem | null;
  onCreated?: (childId: string) => void;
};

export function AddAdvanceBillSlideOver({
  open,
  onClose,
  advance,
  onCreated,
}: AddAdvanceBillSlideOverProps) {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  // Site-roles (Engineer / Supervisor) can submit but not post —
  // their bills land in `pending_approval` for Finance to review.
  // Finance roles post straight to `posted`.
  const workflowStatusForCreate: 'posted' | 'pending_approval' =
    canFinance(orgRole) ? 'posted' : 'pending_approval';

  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  const [pickedParty, setPickedParty] = useState<{
    id: string;
    name: string;
    partyType: PartyType;
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Default category is 'material' — the most common bill type
  // staff file against an advance ("plywood from xyz shop").
  const [category, setCategory] = useState<TransactionCategory | ''>('material');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live read of existing children so we can show the running
  // balance in the header — gives the user a constant sense of
  // "how much is still in the wallet I'm spending from".
  const { children } = useAdvanceChildren(
    advance?.orgId ?? null,
    advance?.id ?? null,
  );
  const liveBalance = useMemo(() => {
    if (!advance) return null;
    return computeAdvanceBalance(
      { amount: advance.amount ?? 0, advanceStatus: advance.advanceStatus },
      children.map((c) => ({
        type: c.type,
        amount: c.amount ?? 0,
        advanceRole: c.advanceRole,
        workflowStatus: c.workflowStatus,
      })),
    );
  }, [advance, children]);

  // Reset on open or when the target advance changes.
  useEffect(() => {
    if (!open) return;
    setAmount('');
    setDate(todayIso());
    setPickedParty(null);
    setPickerOpen(false);
    setCategory('material');
    setPaymentMethod('cash');
    setReferenceNumber('');
    setDescription('');
    setBusy(false);
    setError(null);
  }, [open, advance?.id]);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!advance) return setError('No advance selected.');
    if (!user) return setError('You must be signed in.');
    const num = Number(amount.replace(/,/g, ''));
    if (!num || Number.isNaN(num) || num <= 0) {
      return setError('Enter a valid amount greater than zero.');
    }
    if (!pickedParty) return setError('Pick the vendor you paid.');
    if (!category) return setError('Pick a category for this bill.');
    if (category === 'staff_advance')
      return setError("A bill can't itself be a staff advance.");

    setBusy(true);
    try {
      const id = await addAdvanceSettlement({
        parentTransactionId: advance.id,
        orgId: advance.orgId,
        projectId: advance.projectId,
        vendorPartyId: pickedParty.id,
        vendorPartyName: pickedParty.name,
        amount: num,
        category: category as Exclude<TransactionCategory, 'staff_advance'>,
        description: description.trim() || undefined,
        paymentMethod: paymentMethod || undefined,
        referenceNumber: referenceNumber.trim() || undefined,
        date: new Date(`${date}T00:00:00`),
        createdBy: user.uid,
        workflowStatus: workflowStatusForCreate,
      });
      onCreated?.(id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save bill.');
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label="Add bill against advance"
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">Add bill against advance</h2>
          <button
            type="button"
            className="slide-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {/* Advance context card — always-visible header so the
              user knows whose wallet they're spending from. */}
          {advance ? (
            <div
              className="card"
              style={{
                padding: 14,
                marginBottom: 14,
                background: '#FEF3C7',
                border: '1px solid #FDE68A',
                color: '#92400E',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 4,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
                  ADVANCE WALLET
                </div>
                <div style={{ fontSize: 11, fontWeight: 600 }}>
                  Given to {advance.partyName ?? '—'}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <div style={{ fontSize: 13 }}>
                  Given: <strong>{fmtInr(advance.amount)}</strong>
                  {liveBalance ? (
                    <>
                      {' · '}Filed: <strong>{fmtInr(liveBalance.spent)}</strong>
                    </>
                  ) : null}
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: liveBalance && liveBalance.balance < 0 ? 'var(--critical)' : '#92400E',
                  }}
                >
                  {liveBalance ? fmtInr(Math.abs(liveBalance.balance)) : '—'}
                  {liveBalance && liveBalance.balance < 0 ? ' over' : ' left'}
                </div>
              </div>
            </div>
          ) : null}

          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
                marginBottom: 12,
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div>
              <label style={STYLE_LABEL}>
                Amount <span style={STYLE_REQUIRED}>*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                style={{
                  ...STYLE_INPUT,
                  fontSize: 18,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value)}
                disabled={busy}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>
                  Date <span style={STYLE_REQUIRED}>*</span>
                </label>
                <input
                  type="date"
                  style={STYLE_INPUT}
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Payment method</label>
                <select
                  style={STYLE_INPUT}
                  value={paymentMethod}
                  onChange={(e) =>
                    setPaymentMethod(e.currentTarget.value as PaymentMethod | '')
                  }
                  disabled={busy}
                >
                  <option value="">Select method</option>
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={STYLE_GROUP}>
              <label style={STYLE_LABEL}>
                Vendor <span style={STYLE_REQUIRED}>*</span>
              </label>
              {pickedParty ? (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={busy}
                  style={{
                    ...STYLE_INPUT,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                  }}
                >
                  <PartyAvatar party={pickedParty} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pickedParty.name}
                    </div>
                    <div
                      style={{ fontSize: 11, color: 'var(--text-secondary)' }}
                    >
                      {partyTypeLabel(pickedParty.partyType)} ·{' '}
                      <span style={{ color: 'var(--brand)', fontWeight: 600 }}>
                        Change
                      </span>
                    </div>
                  </div>
                  <i
                    className="ph-bold ph-caret-down"
                    style={{ color: 'var(--text-tertiary)' }}
                  />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={busy}
                  style={{
                    ...STYLE_INPUT,
                    cursor: 'pointer',
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                  }}
                >
                  <span>
                    <i className="ph ph-magnifying-glass" /> Pick the vendor
                  </span>
                  <i className="ph-bold ph-caret-down" />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>
                  Category <span style={STYLE_REQUIRED}>*</span>
                </label>
                <select
                  style={STYLE_INPUT}
                  value={category}
                  onChange={(e) =>
                    setCategory(e.currentTarget.value as TransactionCategory | '')
                  }
                  disabled={busy}
                >
                  {/* staff_advance is excluded — a bill CAN'T itself
                      be an advance (no nesting). */}
                  {TRANSACTION_CATEGORY_OPTIONS.filter(
                    (o) => o.value !== 'staff_advance',
                  ).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Reference / Bill no.</label>
                <input
                  type="text"
                  placeholder="e.g. INV-1234"
                  style={STYLE_INPUT}
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div style={STYLE_GROUP}>
              <label style={STYLE_LABEL}>Description</label>
              <textarea
                rows={3}
                placeholder="What was purchased — e.g. 25mm plywood × 6 sheets"
                style={{ ...STYLE_INPUT, resize: 'vertical' }}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                padding: 10,
                background: '#FAFAFA',
                borderRadius: 6,
                border: '1px solid var(--border)',
                marginTop: 12,
              }}
            >
              📎 Receipt photo upload ships in the next iteration. For
              now, save and attach from the detail screen later.
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 6,
                paddingTop: 16,
                borderTop: '1px solid var(--border)',
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  background: 'var(--critical)',
                  borderColor: 'var(--critical)',
                }}
                disabled={busy || !advance}
              >
                {busy ? 'Saving…' : 'Save bill'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <PartyPickerSlideOver
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        orgId={advance?.orgId ?? null}
        defaultName=""
        onPick={(p) => {
          setPickedParty(p);
          setPickerOpen(false);
        }}
      />
    </>
  );
}

function PartyAvatar({
  party,
}: {
  party: { name: string; partyType: PartyType };
}) {
  const c = partyTypeColors(party.partyType);
  const parts = party.name.trim().split(/\s+/).slice(0, 2);
  const ini = parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: c.bg,
        color: c.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {ini}
    </div>
  );
}
